import React, { forwardRef } from 'react';
import {
  PALETTE, FONT_SERIF, FONT_SANS, FONT_MONO,
  HaleMark, ToxIntelMark, AgMark, Backplate, Card, Eyebrow, Headline, FlowArrowH, FlowArrowV, Rule,
} from './primitives';

/* Asset 3 — Hale Governance Workflow
 *
 * Lateral system diagram showing the chain of accountability:
 *   Brand → Hale → ToxIntel Review Infrastructure → Scientific Assessment
 *                → Approval Decision → Dashboard Status
 *
 * This is the asset Hale uses to explain "who does what" to retailers
 * and investors. The visual makes the separation of concerns explicit. */

const VB_W = 1920;
const VB_H = 1080;

const NODES = [
  { code: 'BRAND',  label: 'Brand',          sub: 'Applicant · portfolio · documentation', accent: PALETTE.MUTED },
  { code: 'HALE',   label: 'Hale',           sub: 'Brand-activation layer · pathways · badges', accent: PALETTE.HALE_RED },
  { code: 'TI',     label: 'ToxIntel',       sub: 'Review infrastructure · governance system', accent: PALETTE.CORAL },
  { code: 'REVIEW', label: 'Scientific Assessment', sub: 'Independent toxicologist · audit trail', accent: PALETTE.CORAL },
  { code: 'DEC',    label: 'Approval Decision',     sub: 'Reviewer sign-off · escalation routing', accent: PALETTE.SLATE },
  { code: 'DASH',   label: 'Dashboard Status',      sub: 'Live monitoring · outstanding items', accent: PALETTE.SLATE },
];

const GovernanceWorkflowAsset = forwardRef(function GovernanceWorkflowAsset(_props, ref) {
  // 6 nodes arranged in two rows of three — clean executive layout.
  const colW = 540;
  const gap = 60;
  const positions = [
    { x: 100,                       y: 360 }, // BRAND
    { x: 100 + (colW + gap),        y: 360 }, // HALE
    { x: 100 + (colW + gap) * 2,    y: 360 }, // TI
    { x: 100,                       y: 700 }, // REVIEW
    { x: 100 + (colW + gap),        y: 700 }, // DEC
    { x: 100 + (colW + gap) * 2,    y: 700 }, // DASH
  ];
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Hale governance workflow asset"
    >
      <Backplate w={VB_W} h={VB_H} />

      {/* Header */}
      <ToxIntelMark x={64} y={70} fontSize={28} />
      <HaleMark x={210} y={42} w={90} h={38} fontSize={26} />
      <AgMark x={340} y={70} fontSize={22} />
      <text x={VB_W - 64} y={70} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        GOVERNANCE WORKFLOW · CHAIN OF ACCOUNTABILITY
      </text>
      <Rule x1={0} x2={VB_W} y={108} />

      <Eyebrow x={100} y={170} text="The Hale governance workflow" />
      <Headline x={100} y={236} text="A clear chain of accountability." size={56} />
      <Headline x={100} y={296} text="Brand → Hale → ToxIntel → independent review." size={32} italic color={PALETTE.CORAL} />

      {/* Nodes */}
      {NODES.map((n, i) => {
        const { x, y } = positions[i];
        return (
          <g key={n.code} transform={`translate(${x},${y})`}>
            <Card x={0} y={0} w={colW} h={220} fill="rgba(255,255,255,0.7)">
              <g transform="translate(32,32)">
                <rect x={0} y={0} width={70} height={26} fill={PALETTE.SLATE} rx={2} />
                <text x={35} y={17} textAnchor="middle" fill={PALETTE.IVORY} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
                  {n.code}
                </text>
                <text x={0} y={84} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={800} fontSize={42}>
                  {n.label}
                </text>
                <text x={0} y={130} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={16}>
                  {n.sub}
                </text>
                <rect x={0} y={150} width={60} height={3} fill={n.accent} />
              </g>
            </Card>
          </g>
        );
      })}

      {/* Horizontal arrows between row 1 nodes */}
      <FlowArrowH x={100 + colW + 8} y={470} w={gap - 16} />
      <FlowArrowH x={100 + (colW + gap) + colW + 8} y={470} w={gap - 16} />
      {/* Horizontal arrows between row 2 nodes */}
      <FlowArrowH x={100 + colW + 8} y={810} w={gap - 16} />
      <FlowArrowH x={100 + (colW + gap) + colW + 8} y={810} w={gap - 16} />
      {/* Wrap arrow row1 → row2 (right edge → left edge) */}
      <g stroke={PALETTE.CORAL} fill="none" strokeWidth={2} strokeDasharray="6 6">
        <path d={`M ${100 + (colW + gap) * 2 + colW + 20} 580 H ${VB_W - 50} V 640 H 80 V 700`} />
      </g>
      <polygon points={`80,700 72,690 88,690`} fill={PALETTE.CORAL} />

      {/* Footer caption */}
      <Rule x1={100} x2={VB_W - 100} y={VB_H - 80} />
      <text x={100} y={VB_H - 40} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={14}>
        Hale activates the program. ToxIntel runs the review infrastructure.
        The Agyekum Group provides the independent scientific review.
      </text>
      <text x={VB_W - 100} y={VB_H - 40} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        HALE × TOXINTEL · 2026
      </text>
    </svg>
  );
});

export default GovernanceWorkflowAsset;
